import { useEffect, useState, useMemo, useCallback, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import {
  clearEngagement,
  fetchEngagement,
  selecthasMoreEngagement,
  selectEngagement,
  selectEngagementLoading,
  selectAuthors,
} from "../../../../store/slices/article-engagements";
import AuditAnalyticsHeader from "../audit-analytics-header";
import "../../styles.css";
import InfinityTable from "../../../../components/infinity-table/infinity-table";
import ApiSubjectsManagement from "../../../../api/subjects-manager";
import { Body } from "../../../../components/main-body/body";
import classNames from "classnames";
import "../../../../components/main-body/body.css";
import { Progress } from "antd";
import ApiCategoryManagement from "../../../../api/category-managements";
import ApiArticles from "../../../../api/articles";
import ApiSavedViews from "../../../../api/saved-views";
import ArticleEngagementCard from "./article-engagement-card/article-engagement-card";
import { useLocation } from "react-router-dom";
import { useProjectNavigate } from "../../../../common/utils/useProjectNavigate";
import ApiSubjectsDashboard from "../../../../api/subjects-dashboard";
import moment from "moment";
import { selectSettings } from "../../../../store/slices/settings";
import { selectCurrentProject } from "../../../../store/slices/user";

const sortViews = (list = []) =>
  [...list].sort((a, b) => {
    if (Boolean(a.isDefault) !== Boolean(b.isDefault)) {
      return a.isDefault ? -1 : 1;
    }

    return String(a.name || "").localeCompare(String(b.name || ""));
  });

const normalizeSorting = (sorting) => ({
  sortField: sorting?.sortField || null,
  sortOrder: sorting?.sortOrder || null,
});

const ArticleEngagementTable = ({ navigation }) => {
  const engagements = useSelector(selectEngagement);
  const authors = useSelector(selectAuthors);
  const hasMore = useSelector(selecthasMoreEngagement);
  const isLoading = useSelector(selectEngagementLoading);
  const settings = useSelector(selectSettings);
  const currentProject = useSelector(selectCurrentProject);

  const dispatch = useDispatch();
  const { t } = useTranslation();
  const navigate = useProjectNavigate();
  const location = useLocation();

  const defaultFiltersRef = useRef({
    period: [
      {
        start: moment().subtract(30, "days").utc().toISOString(),
        end: moment().utc().toISOString(),
      },
    ],
  });

  const defaultFilters = defaultFiltersRef.current;

  const sortingOptions = [
    { value: "title", label: "Title" },
    { value: "likes", label: "Likes" },
    { value: "dislikes", label: "Dislikes" },
    { value: "sentimentRatio", label: "Sentiment ratio" },
  ];

  const [page, setPage] = useState(1);
  const [filters, setFilters] = useState(null);
  const [filtersReady, setFiltersReady] = useState(false);

  const [sorting, setSorting] = useState({
    sortField: null,
    sortOrder: null,
  });

  const [subjectsList, setSubjectsList] = useState([]);
  const [categoriesList, setCategoriesList] = useState([]);
  const [tags, setTags] = useState([]);
  const [searchValue, setSearchValue] = useState("");
  const [isOpen, setOpen] = useState(false);
  const [selectedEngagement, setSelectedEngagement] = useState({});
  const [cohortsList, setCohortsList] = useState([]);
  const [sitesList, setSitesList] = useState([]);

  const [savedViewsItems, setSavedViewsItems] = useState([]);
  const [savedViewsLoading, setSavedViewsLoading] = useState(false);
  const [selectedViewId, setSelectedViewId] = useState(null);

  const isSitesEnabled =
    settings?.find((s) => s.key === "sitesEnable")?.value === true;

  const SAVED_VIEW_SCOPE = "analytics_articles";

  const safeFilters = filters || defaultFilters;

  const handleNavigate = (path) => {
    if (location.pathname !== path) {
      navigate(path);
    }
  };

  const getDashboardFilterItems = () => {
    ApiSubjectsDashboard.filterField().then((r) => {
      setCohortsList(
        r.data.cohorts?.map((c) => ({ id: c.id, name: c.value })) || []
      );
      setSitesList(
        r.data.sites?.map((c) => ({ id: c.id, name: c.name })) || []
      );
    });
  };

  const handleSelectEngagement = (article) => {
    handleNavigate(`/analytics/monitoring/articles/${article.id}`);
    setSelectedEngagement(article);
    setOpen(true);
  };

  const fetchSubjectList = () => {
    ApiSubjectsManagement.getSubjectsSimple().then((r) => {
      setSubjectsList(
        (r.data.subjects || []).map((s) => ({
          id: s.id,
          name: s.displayId,
        }))
      );
    });
  };

  useEffect(() => {
    fetchSubjectList();
    getDashboardFilterItems();
  }, []);

  useEffect(() => {
    const pathParts = location.pathname.split("/");
    const articleId = pathParts[pathParts.length - 1];

    if (articleId && articleId !== "articles") {
      setSelectedEngagement({ id: articleId });
      setOpen(true);
    } else {
      setOpen(false);
      setSelectedEngagement({});
    }
  }, [location.pathname]);

  const getTags = useCallback(async () => {
    const response = await ApiArticles.getTags();
    if (response.data?.tags) {
      setTags(response.data.tags);
    }
  }, []);

  useEffect(() => {
    getTags();
  }, [getTags]);

  const closeTaskDrawer = () => {
    handleNavigate(`/analytics/monitoring/articles`);
    setOpen(false);
    setSelectedEngagement({});
  };

  const fetchData = (
    currentPage = page,
    query = searchValue,
    currentFilters = safeFilters,
    reset = false
  ) => {
    if (!currentFilters) return;

    if (reset) {
      dispatch(clearEngagement());
    }

    dispatch(
      fetchEngagement({
        query,
        page: currentPage,
        ...currentFilters,
        createdAtEnd:
          currentFilters.period?.[currentFilters.period.length - 1]?.end,
        createdAtStart:
          currentFilters.period?.[currentFilters.period.length - 1]?.start,
      })
    );

    setPage(currentPage + 1);
  };

  const handleUpdateSearchValue = (value) => {
    setSearchValue(value);
    setPage(1);
    fetchData(1, value, safeFilters, true);
  };

  const fetchCategoryList = () => {
    ApiCategoryManagement.getCategories().then((r) => {
      setCategoriesList(
        (r.data.categories || []).map((s) => ({
          id: s.id,
          name: s.name,
        }))
      );
    });
  };

  useEffect(() => {
    fetchCategoryList();
  }, []);

  const loadSavedViews = useCallback(async () => {
    if (!currentProject?.id) {
      setSavedViewsItems([]);
      setSelectedViewId(null);
      setFilters(defaultFilters);
      setSorting({ sortField: null, sortOrder: null });
      setFiltersReady(true);
      return;
    }

    setSavedViewsLoading(true);

    try {
      const { data } = await ApiSavedViews.getSavedViews({
        projectId: currentProject.id,
        scope: SAVED_VIEW_SCOPE,
      });

      const list = sortViews(data?.savedViews || []);
      setSavedViewsItems(list);

      const defaultView = list.find((item) => item.isDefault);

      if (defaultView) {
        setSelectedViewId(defaultView.id);
        setFilters(defaultView.filters || {});
        setSorting(normalizeSorting(defaultView.sorting));
      } else {
        setSelectedViewId(null);
        setFilters(defaultFilters);
        setSorting({ sortField: null, sortOrder: null });
      }
    } catch (error) {
      console.error("Failed to load saved views", error);
      setSavedViewsItems([]);
      setSelectedViewId(null);
      setFilters(defaultFilters);
      setSorting({ sortField: null, sortOrder: null });
    } finally {
      setSavedViewsLoading(false);
      setFiltersReady(true);
      setPage(1);
    }
  }, [currentProject?.id, defaultFilters]);

  useEffect(() => {
    loadSavedViews();
  }, [loadSavedViews]);

  const updateFilterValue = (filter) => {
    setFilters(filter);
    setPage(1);
  };

  const resetFilters = () => {
    setSelectedViewId(null);
    setFilters(defaultFilters);
    setSorting({ sortField: null, sortOrder: null });
    setPage(1);
  };

  const applySavedViewFilters = (savedState, viewId = null) => {
    setSelectedViewId(viewId);
    setFilters(
      savedState?.filters && Object.keys(savedState.filters).length
        ? savedState.filters
        : {}
    );
    setSorting(normalizeSorting(savedState?.sorting));
    setPage(1);
  };

  useEffect(() => {
    if (!filtersReady || !filters) return;

    dispatch(clearEngagement());
    fetchData(1, searchValue, filters, true);
  }, [filtersReady, filters]);

  const getComparableValue = (item, sortField) => {
    switch (sortField) {
      case "title":
        return String(item?.title || "").toLowerCase();

      case "likes":
        return Number(item?.likesCount || 0);

      case "dislikes":
        return Number(item?.dislikesCount || 0);

      case "sentimentRatio":
        return Number(item?.likesPercent || 0);

      default:
        return "";
    }
  };

  const sortedEngagements = useMemo(() => {
    if (!sorting?.sortField || !sorting?.sortOrder) {
      return engagements;
    }

    const sorted = [...(engagements || [])].sort((a, b) => {
      const aVal = getComparableValue(a, sorting.sortField);
      const bVal = getComparableValue(b, sorting.sortField);

      if (aVal < bVal) return sorting.sortOrder === "ASC" ? -1 : 1;
      if (aVal > bVal) return sorting.sortOrder === "ASC" ? 1 : -1;
      return 0;
    });

    return sorted;
  }, [engagements, sorting]);

  const handleTableChange = (_, __, sorter) => {
    if (!sorter?.field) {
      setSorting({ sortField: null, sortOrder: null });
      return;
    }

    setSorting({
      sortField: sorter.field,
      sortOrder: sorter.order === "ascend" ? "ASC" : "DESC",
    });
  };

  const columns = [
    {
      title: t("engagement.table.title"),
      dataIndex: "title",
      sorter: true,
      sortOrder:
        sorting.sortField === "title"
          ? sorting.sortOrder === "ASC"
            ? "ascend"
            : "descend"
          : null,
      render: (_, engagement) => (
        <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
          <div>
            <span style={{ fontWeight: "bold" }}>
              {engagement.title || "N/A"}
            </span>
            <div style={{ fontSize: "12px", color: "#888" }}>
              {engagement?.Category?.name}
            </div>
          </div>
        </div>
      ),
      key: "title",
      width: "55%",
    },
    {
      title: t("engagement.table.likes"),
      dataIndex: "likes",
      sorter: true,
      sortOrder:
        sorting.sortField === "likes"
          ? sorting.sortOrder === "ASC"
            ? "ascend"
            : "descend"
          : null,
      render: (_, engagement) => <span>{engagement.likesCount || 0}</span>,
      key: "likes",
      width: "10%",
    },
    {
      title: t("engagement.table.dislikes"),
      dataIndex: "dislikes",
      sorter: true,
      sortOrder:
        sorting.sortField === "dislikes"
          ? sorting.sortOrder === "ASC"
            ? "ascend"
            : "descend"
          : null,
      render: (_, engagement) => <span>{engagement.dislikesCount || 0}</span>,
      key: "dislikes",
      width: "10%",
    },
    {
      title: t("engagement.table.sentiment_ratio"),
      dataIndex: "sentimentRatio",
      sorter: true,
      sortOrder:
        sorting.sortField === "sentimentRatio"
          ? sorting.sortOrder === "ASC"
            ? "ascend"
            : "descend"
          : null,
      render: (_, engagement) => {
        const hasData =
          engagement.likesPercent > 0 || engagement.dislikesPercent > 0;

        return (
          <div style={{ display: "flex", alignItems: "center" }}>
            {hasData ? (
              <>
                <span
                  style={{ marginRight: 8, width: 40, textAlign: "right" }}
                >
                  {engagement.likesPercent}%
                </span>
                <Progress
                  size="small"
                  showInfo={false}
                  percent={engagement.likesPercent}
                  strokeColor="#2D7FFA"
                  style={{ flex: 1, marginInlineEnd: 0, marginBottom: 2 }}
                />
                <span style={{ marginLeft: 8, width: 40, textAlign: "left" }}>
                  {engagement.dislikesPercent}%
                </span>
              </>
            ) : (
              <span>N/A</span>
            )}
          </div>
        );
      },
      key: "sentimentRatio",
      width: "25%",
    },
  ];

  const filterItems = [
    { name: "Cohorts", id: "cohortIds", children: cohortsList },
    {
      name: "Date peroid",
      id: "period",
      period: true,
      defaultValue: defaultFilters.period,
    },
    { name: "Tags", id: "tags", children: tags },
    { name: "Category", id: "categoryIds", children: categoriesList },
    {
      name: "Author",
      id: "userIds",
      children: authors.map((x) => ({ id: x.id, name: x.fullName })),
    },
    ...(isSitesEnabled
      ? [{ name: "Sites", id: "siteIds", children: sitesList }]
      : []),
  ];

  const headerProps = useMemo(
    () => ({
      search: {
        value: searchValue,
        onChange: handleUpdateSearchValue,
        placeholder: t("assets.btn.search"),
      },
    }),
    [searchValue, t]
  );

  return (
    <Body disabledPaddings={true}>
      <AuditAnalyticsHeader
        title={t("engagement.articles")}
        filterItems={filterItems}
        updateFilterValue={updateFilterValue}
        header={headerProps}
        filters={safeFilters}
        sortingOptions={sortingOptions}
        savedViews={{
          enabled: true,
          projectId: currentProject?.id,
          scope: SAVED_VIEW_SCOPE,
          filters: safeFilters,
          sorting,
          defaultFilters,
          defaultSorting: { sortField: null, sortOrder: null },
          items: savedViewsItems,
          loading: savedViewsLoading,
          selectedViewId,
          onSelectedViewChange: setSelectedViewId,
          onApplySavedView: applySavedViewFilters,
          onResetFilters: resetFilters,
          onReloadSavedViews: loadSavedViews,
          disabled: isLoading || savedViewsLoading,
        }}
      />

      <div className={classNames("lf-content")}>
        <InfinityTable
          data={sortedEngagements}
          fetchData={() => fetchData(page, searchValue, safeFilters)}
          hasMore={hasMore}
          tableColumns={columns}
          isLoading={isLoading}
          before={navigation}
          onHandleRowClick={handleSelectEngagement}
          onChange={handleTableChange}
        />
      </div>

      <ArticleEngagementCard
        engagement={selectedEngagement}
        isOpen={isOpen}
        close={closeTaskDrawer}
        subjects={subjectsList}
        cohorts={cohortsList}
        initialPeriodFilter={
          safeFilters.hasOwnProperty("period") ? { period: safeFilters.period } : {}
        }
        initialCohortsFilter={
          safeFilters.hasOwnProperty("cohortIds")
            ? { cohortIds: safeFilters.cohortIds }
            : []
        }
      />
    </Body>
  );
};

export default ArticleEngagementTable;